import React from 'react';
import {
    DefaultButton,
    getTheme,
    Icon,
    MessageBar,
    MessageBarType,
    Panel,
    PanelType,
    PrimaryButton,
    Stack,
    Text,
    TextField,
    Toggle
} from '@fluentui/react';
import { IExtensionFolio } from '../../models/Extension';
import { useSaveCatalogFolioMutation } from '../../store/endpoints/FolioAdminEndpoints';
import FolioForm from './FolioForm';
import EditableText from '../../components/Inputs/EditableText';
import ImageUploader from '../../components/Inputs/ImageUploader';
import { hasErrors, IFolioIssue, parseFolioDocument, validateFolio } from './folioValidation';
import { FOLIO_EDITOR_STRINGS as S } from './folioEditorStrings';

// Editing one folio, in either of two views over the same document.
//
// The form is the interface; raw JSON is the escape hatch for the fields the form does not model and for
// pasting a definition wholesale. They share one piece of state -- the folio object -- so switching views
// is not a save and nothing is lost either way. The one asymmetry is deliberate: leaving raw mode has to
// parse, and unparseable JSON keeps you there rather than silently discarding what you typed.
//
// Both views run the same validation, and errors block Save in both. The server re-validates regardless;
// this only turns a round-trip 400 into something anchored to the field that caused it.

const theme = getTheme();

const emptyFolio = (id: string): IExtensionFolio => ({
    id,
    name: '',
    subExtensions: {},
    sharedFields: [],
    defaultSubExtensions: [],
    supportsSubOrganization: false,
});

const IssueList: React.FC<{ issues: IFolioIssue[] }> = ({ issues }) => {
    if (issues.length === 0) return null;
    const errors = issues.filter(i => i.level === 'error');
    const warnings = issues.filter(i => i.level === 'warning');
    return (
        <Stack tokens={{ childrenGap: '0.4em' }}>
            {errors.length > 0 && (
                <MessageBar messageBarType={MessageBarType.error}>
                    <Stack tokens={{ childrenGap: '0.25em' }}>
                        {errors.map((issue, i) => (
                            <Text key={i} variant='small'>
                                {issue.where ? `${issue.where}: ` : ''}{issue.message}
                            </Text>
                        ))}
                    </Stack>
                </MessageBar>
            )}
            {warnings.length > 0 && (
                <MessageBar messageBarType={MessageBarType.warning}>
                    <Stack tokens={{ childrenGap: '0.25em' }}>
                        {warnings.map((issue, i) => (
                            <Text key={i} variant='small'>
                                {issue.where ? `${issue.where}: ` : ''}{issue.message}
                            </Text>
                        ))}
                    </Stack>
                </MessageBar>
            )}
        </Stack>
    );
};

export interface IFolioEditorPanelProps {
    folio?: IExtensionFolio;
    isNew: boolean;
    /** Prefix the server requires on an author's new folio ids. Empty for a global admin. */
    newIdPrefix: string;
    isGlobalAdmin: boolean;
    onClose: () => void;
}

const FolioEditorPanel: React.FC<IFolioEditorPanelProps> = ({
    folio: initial, isNew, newIdPrefix, isGlobalAdmin, onClose
}) => {
    const [id, setId] = React.useState(initial?.id ?? newIdPrefix);
    const [folio, setFolio] = React.useState<IExtensionFolio>(initial ?? emptyFolio(newIdPrefix));
    const [mode, setMode] = React.useState<'form' | 'raw'>('form');
    const [rawText, setRawText] = React.useState(() => JSON.stringify(initial ?? emptyFolio(newIdPrefix), null, 2));
    const [rawError, setRawError] = React.useState<string | undefined>();
    const [saveError, setSaveError] = React.useState<string | undefined>();
    const [save, saveState] = useSaveCatalogFolioMutation();

    const issues = React.useMemo(() => validateFolio(folio, id), [folio, id]);
    const blocked = hasErrors(issues) || !!rawError;

    /** Form edits are the source of truth; raw text is regenerated when the view is opened. */
    const enterRaw = () => {
        setRawText(JSON.stringify(folio, null, 2));
        setRawError(undefined);
        setMode('raw');
    };

    /** Leaving raw mode parses. Unparseable JSON keeps the author here rather than losing their edit. */
    const leaveRaw = (): boolean => {
        const parsed = parseFolioDocument(rawText);
        if (parsed.error || !parsed.folio) {
            setRawError(S.invalidJson(parsed.error ?? ''));
            return false;
        }
        setFolio(parsed.folio);
        setRawError(undefined);
        return true;
    };

    const onRawChange = (text: string) => {
        setRawText(text);
        // Lint as they type so the issue list below is about what is on screen, not the last valid parse.
        const parsed = parseFolioDocument(text);
        if (parsed.error || !parsed.folio) {
            setRawError(S.invalidJson(parsed.error ?? ''));
            return;
        }
        setRawError(undefined);
        setFolio(parsed.folio);
    };

    const submit = async () => {
        setSaveError(undefined);
        if (mode === 'raw' && !leaveRaw()) return;
        if (!id.trim()) {
            setSaveError(S.idRequired);
            return;
        }
        if (hasErrors(validateFolio(folio, id))) return;

        const result = await save({ id: id.trim(), folio: { ...folio, id: id.trim() }, create: isNew });
        if ('error' in result) {
            // The server's own message names the rule that was broken, which is more useful than ours.
            const data = (result.error as { data?: { message?: string } })?.data;
            setSaveError(data?.message ?? S.saveFailed);
            return;
        }
        onClose();
    };

    return (
        <Panel
            isOpen
            onDismiss={onClose}
            type={PanelType.large}
            closeButtonAriaLabel='Close'
            // The two things that identify a folio, in the one place every tab can see. They were the
            // first two fields of the Basics tab, which meant the panel's own title said "Edit tdsynnex"
            // while the thing being edited sat below it saying the same word — and neither was visible
            // once the author moved to another tab.
            onRenderHeader={() => (
                <Stack horizontal tokens={{ childrenGap: '1em' }} verticalAlign='start'
                    styles={{ root: { width: '100%', padding: '0 24px 0 24px' } }}>
                    <ImageUploader
                        width={100}
                        height={100}
                        imageId={`folio_${id || 'new'}_logo`}
                        imageType='extensionFolioLogo'
                        value={folio.image ?? ''}
                        onChange={(v) => setFolio({ ...folio, image: v ?? '' })}
                        onClear={() => setFolio({ ...folio, image: '' })}
                    />
                    <Stack.Item grow>
                        <EditableText
                            value={folio.name ?? ''}
                            placeholder={S.namePlaceholder}
                            valueRequired
                            fontSize='xLarge'
                            onChange={(v) => setFolio({ ...folio, name: v ?? '' })}
                        />
                        {/* The id, which on a new folio is being typed below and updates as it goes. */}
                        <Text variant='small' style={{ color: theme.palette.neutralSecondary }}>
                            {id || S.newTitle}
                        </Text>
                    </Stack.Item>
                </Stack>
            )}
            isFooterAtBottom
            // A pinned footer floats over the scrolling body and Fluent leaves it transparent, so the
            // buttons read as hovering over the form. Same treatment as the other pinned-footer panels.
            styles={{
                footer: {
                    backgroundColor: theme.palette.white,
                    borderTop: `1px solid ${theme.palette.neutralLight}`,
                },
            }}
            onRenderFooterContent={() => (
                <Stack horizontal tokens={{ childrenGap: '0.5em' }} verticalAlign='center'>
                    <PrimaryButton
                        text={saveState.isLoading ? S.saving : S.save}
                        disabled={saveState.isLoading || blocked}
                        onClick={submit}
                    />
                    <DefaultButton text={S.cancel} onClick={onClose} disabled={saveState.isLoading} />
                    {blocked && (
                        <Text variant='small' style={{ color: theme.palette.neutralSecondary }}>
                            {S.blockedHint}
                        </Text>
                    )}
                </Stack>
            )}
        >
            <Stack tokens={{ childrenGap: '1em' }}>
                {/* Only while it can still be set. On a saved folio the id is fixed, and a disabled box
                    repeating what the header already shows is a field that exists to be ignored. */}
                {isNew && <TextField
                    label={S.idLabel}
                    value={id}
                    onChange={(_e, v) => setId(v ?? '')}
                    description={S.idNewDescription(newIdPrefix)}
                    styles={{ root: { maxWidth: 420 } }}
                />}

                {saveError && <MessageBar messageBarType={MessageBarType.error}>{saveError}</MessageBar>}

                {/* A toggle, not a second tab strip: the form is itself tabbed now, and two rows of tabs
                    made the raw view look like a seventh section rather than another way to see all six.
                    The transitions are the same as before — opening raw regenerates the text from the
                    form, and leaving it only succeeds if what was typed still parses. */}
                <Toggle
                    label={S.showRaw}
                    inlineLabel
                    checked={mode === 'raw'}
                    onChange={(_e, checked) => {
                        if (checked && mode !== 'raw') enterRaw();
                        else if (!checked && leaveRaw()) setMode('form');
                    }}
                    styles={{ root: { marginBottom: 0 } }}
                />

                {rawError && <MessageBar messageBarType={MessageBarType.error}>{rawError}</MessageBar>}
                <IssueList issues={issues} />

                {mode === 'form' ? (
                    <FolioForm folio={folio} onChange={setFolio} isGlobalAdmin={isGlobalAdmin} />
                ) : (
                    <Stack tokens={{ childrenGap: '0.5em' }}>
                        <Stack horizontal verticalAlign='center' tokens={{ childrenGap: '0.4em' }}>
                            <Icon iconName='Info' style={{ color: theme.palette.neutralSecondary }} />
                            <Text variant='small' style={{ color: theme.palette.neutralSecondary }}>
                                {S.rawHelp}
                            </Text>
                        </Stack>
                        <TextField
                            label={S.documentLabel}
                            multiline
                            rows={28}
                            value={rawText}
                            onChange={(_e, v) => onRawChange(v ?? '')}
                            styles={{ field: { fontFamily: 'Consolas, Menlo, monospace', fontSize: 12 } }}
                        />
                    </Stack>
                )}
            </Stack>
        </Panel>
    );
};

export default FolioEditorPanel;
